import mixpanel from "mixpanel-browser";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../Providers/Auth";
import { cn } from "../../../../lib/utils";
import { GlobalBranding } from "../../../../Components/Navbar/Navbar";
import { Button } from "../../../../shadcnComponents/ui/button";
import {
  ContentChipsArray,
  NotLoginChapterMessage,
  StackAvatars,
} from "./molecules";
import { Check, Linkedin, Loader, SquarePlus } from "lucide-react";
import { FaRegShareFromSquare } from "react-icons/fa6";
import { formatFollowersCount } from "@/Helpers/format-helper";
import { BadgeChips, ChapterFaqs, GradientImage } from "./atoms";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shadcnComponents/ui/accordion";

export const Header = ({
  theme = "dark",
  bg,
  hideUser,
  hideName = false,
}: {
  theme?: "dark" | "light";
  bg?: string;
  hideUser?: boolean;
  hideName?: boolean;
}) => {
  const navigate = useNavigate();

  const authState = useAuth();

  return (
    <div
      className={cn(
        `w-full box-border px-[4%] md:px-[40px] py-3 flex items-center justify-between top-0 left-0 border-b border-b-[#262626]`,
        bg
      )}
    >
      <img
        src={
          theme === "light"
            ? GlobalBranding?.logo?.dark
            : GlobalBranding?.logo?.light
        }
        alt=""
        className="w-20 md:w-32 cursor-pointer"
        onClick={() => {
          navigate("/");
        }}
      />

      <div className="flex gap-5">
        {authState?.loggedUser && (
          <div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                mixpanel.track("Creator Header: Go to Dashboard Clicked");
                navigate("/influencer/dashboard");
              }}
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {!hideUser && (
          <section className="flex items-center gap-3 ">
            {!hideName && (
              <span className="font-public text-sm md:text-xl font-medium text-[#FFF]">
                {authState?.loggedUser?.name}
              </span>
            )}
            <img
              src={
                authState?.loggedUser?.profile ||
                "https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/creatorProfileIcon2.png"
              }
              alt=""
              className="rounded-full md:w-10 md:h-10 w-6 h-6"
            />
          </section>
        )}
      </div>
    </div>
  );
};

export const Footer = () => {
  const [cookies] = useCookies(["referal_code"]);

  return (
    <section className=" md:py-4 py-[20px] md:p-0 flex items-center justify-center">
      <div className="flex flex-col justify-center items-center gap-[20px] w-fit md:w-full md:flex-row md:justify-between">
        <div className="text-[rgba(163,_163,_163,_0.67)] text-center font-[Inter] text-[12px] not-italic font-normal leading-[18px]">
          <span>{"Powered by "}</span>
          <a
            href="http://anchors.in"
            target="_blank"
            className="font-bold hover:underline"
            onClick={() => mixpanel.track("Clicked Powered by Anchors")}
          >
            anchors.in
          </a>
        </div>

        {/* Button */}
        <div className="flex items-center gap-[12px]">
          <span className="text-[#A3A3A3] text-center font-[Inter] text-[14px] not-italic font-normal leading-[18px]">
            Influencer?
          </span>
          <button
            className="flex px-[12px] py-[8px] justify-center items-center gap-[4.75px] self-stretch rounded-[6px] border-[1px] border-solid border-[#FFF] text-[#FFF] text-center font-[Inter] not-italic leading-[normal]"
            onClick={() => {
              mixpanel.track("Create your Collab Profile");
              const url = `/influencer${
                cookies?.referal_code
                  ? "?refer=" + cookies?.referal_code || ""
                  : ""
              }`;
              window.open(url, "_blank");
            }}
          >
            <span className="text-[16px] font-normal">🪄</span>
            <span className="text-[12px] font-semibold">
              Create your Collab Profile
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

interface ChapterCoverProps {
  chapterDetails?: {
    title?: string;
    pageDesc?: string;
    cover?: string;
    logo?: string;
  };
  avatars?: string[];
  totalCount?: number;
  onJoinChapter?: () => void;
  isJoined?: boolean;
  loadingJoin?: boolean;
  onShare?: () => void;
}

export const ChapterCover = ({
  chapterDetails,
  avatars,
  totalCount,
  onJoinChapter,
  isJoined,
  loadingJoin,
  onShare,
}: ChapterCoverProps) => {
  return (
    <div className="relative rounded-lg h-[510px] overflow-clip">
      <img
        src={chapterDetails?.cover}
        alt="cover_image"
        className="absolute rounded-lg size-full object-cover z-[1]"
      />
      <div className="z-[2] absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-t from-[#000] to-transparent"></div>
      <div className="z-[3] absolute bottom-6 md:bottom-12 left-0 right-0">
        <div className="px-[6%] flex gap-16">
          <img
            src={chapterDetails?.logo}
            alt="logo_image"
            className="hidden md:block w-[124px] h-[146px] rounded-xl object-cover"
          />
          <div className="flex flex-col w-full">
            <div className="space-y-3">
              <h1 className="font-[Inter] text-3xl not-italic font-bold leading-[24px] text-white">
                {chapterDetails?.title}
              </h1>
              <p className="font-[Inter] text-sm not-italic font-normal leading-[22px] md:w-4/6 line-clamp-4">
                {chapterDetails?.pageDesc}
              </p>
            </div>
            <div className="mt-8 flex flex-col gap-7 flex-wrap md:flex-row md:justify-between md:items-center">
              <div className="flex gap-5 ">
                <Button
                  variant="destructive"
                  className="gap-2 items-center"
                  onClick={onJoinChapter}
                  disabled={isJoined || loadingJoin}
                >
                  {loadingJoin ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      <span>Joining...</span>
                    </>
                  ) : isJoined ? (
                    <>
                      <Check size={16} />
                      <span>Joined</span>
                    </>
                  ) : (
                    <>
                      <SquarePlus size={16} />
                      <span className="uppercase">Join Chapter</span>
                    </>
                  )}
                </Button>
                <Button
                  variant="secondary"
                  className="gap-2 items-center"
                  onClick={onShare}
                >
                  <FaRegShareFromSquare size={16} />
                  <span className="hidden md:block uppercase">
                    Invite Fellow Creators
                  </span>
                </Button>
              </div>
              <div className="pl-3">
                <StackAvatars
                  avatarUrls={avatars}
                  totalCount={totalCount}
                  variant="default"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface MemberCardProps {
  name: string;
  followers: number;
  badge: string;
  tagline: string;
  contentCatgories: string[];
  className?: string;
  profileImage?: string;
  slug?: string;
  linkedinLink?: string;
}

export const MemberCard = ({
  name,
  followers,
  badge,
  tagline,
  contentCatgories,
  className,
  profileImage,
  linkedinLink,
}: // slug,
MemberCardProps) => {
  return (
    <div
      className={cn(
        "flex flex-col md:flex-row w-full md:max-w-[606.5px] gap-[12px] md:gap-0 justify-between items-center px-5 py-4 border rounded-lg border-solid border-[#313131]",
        className
      )}
    >
      <GradientImage
        image={profileImage}
        altText={name}
        footer={
          <div
            className="w-3/4 flex justify-center items-center gap-2 bg-[rgba(220,38,39,0.20)] px-3 py-1.5 rounded-[5px] text-[rgba(255,255,255,0.90)] text-center text-xs not-italic font-bold leading-[normal] cursor-pointer"
            onClick={() => {
              mixpanel.track("Creator Chapter: Clicked on Linkedin Profile", {
                name,
                linkedinLink,
              });
              if (linkedinLink) {
                if (
                  !linkedinLink.startsWith("http://") &&
                  !linkedinLink.startsWith("https://")
                ) {
                  linkedinLink = "https://" + linkedinLink;
                }
                window.open(linkedinLink, "_blank");
              }
            }}
          >
            <Linkedin size={14} />
            <span> {`${formatFollowersCount(followers)} Followers`} </span>
          </div>
        }
        width={200}
        height={200}
      />

      <div className="w-full md:w-[320px] flex flex-col justify-center items-start gap-4 md:gap-6">
        {badge && <BadgeChips badge={badge} />}

        <div className="flex flex-col items-start gap-1">
          <h3
            className="text-[color:var(--Shade-White,#FFF)] text-center text-lg md:text-2xl not-italic font-bold leading-[normal]"
            // onClick={() => {
            //   mixpanel.track("Creator Chapter: Clicked on Creator Profile");
            //   window.open(`${window.location.origin}/${slug}`, "_blank");
            // }}
          >
            {name}
          </h3>
          <span className="text-[#7F7F7F] text-xs md:text-sm not-italic font-normal leading-[normal]">
            {tagline}
          </span>
        </div>

        <ContentChipsArray ContentArray={contentCatgories} />
      </div>
    </div>
  );
};

export const FAQArrayForChapters = ({
  className,
  chapterName,
}: {
  className?: string;
  chapterName: string;
}) => {
  // Convert ChapterFaqs array to JSON-LD format for structured data
  const chapterFaqs = ChapterFaqs(chapterName);
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: chapterFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="md:w-1/2  w-full">
      <Accordion type="single" collapsible className={cn("w-full", className)}>
        {chapterFaqs.map((faq, index) => (
          <AccordionItem
            className="border px-6 py-1 rounded-lg border-solid border-[#313131] w-full "
            key={index}
            value={`item-${index + 1}`}
          >
            <AccordionTrigger className="text-[color:var(--Shade-White,#FFF)] text-start  text-sm md:text-base not-italic font-bold leading-[normal]">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="text-[#9A9696] text-[13px] not-italic font-normal leading-6 whitespace-pre-wrap">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </div>
  );
};

export const NotLoggedInCard = ({ userCity, handleJoinChapter }) => {
  return (
    <div className="flex gap-8  flex-wrap mt-8 relative">
      <NotLoginChapterMessage
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2  z-[10] "
        userCity={userCity}
        handleJoinChapter={handleJoinChapter}
      />
      <MemberCard
        name="Dummy User"
        followers={180000}
        badge={"Top ENTREPRENEURSHIP Voice"}
        tagline="Building anchors"
        contentCatgories={[
          "Dummy Category",
          "Dummy Category",
          "Dummy Category",
        ]}
        className="blur-[20px] md:hidden "
        profileImage="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/1720611501997-creator-homepage-linkedin-analyzer-preview.png"
      />
      {Array.from({ length: 4 }).map((_, index) => (
        <div className="w-full md:flex-1 hidden md:flex flex-wrap " key={index}>
          <MemberCard
            name="Dummy User"
            followers={180000}
            badge={"Top ENTREPRENEURSHIP Voice"}
            tagline="Building anchors"
            contentCatgories={[
              "Dummy Category",
              "Dummy Category",
              "Dummy Category",
            ]}
            className="blur-[20px]"
            profileImage="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/1720611501997-creator-homepage-linkedin-analyzer-preview.png"
          />
        </div>
      ))}
    </div>
  );
};

export const ChapterDetailsLoadingSkeleton = () => {
  return (
    <>
      <div className="mx-[10%] py-10">
        <div className="relative rounded-lg h-[510px] overflow-clip">
          <div className="animate-pulse rounded-lg h-[510px] bg-[#313131] w-full"></div>
          <div className="z-[2] absolute bottom-6 md:bottom-12 left-0 right-0">
            <div className="px-[6%] flex gap-16">
              <div className="hidden md:block w-[124px] h-[146px] rounded-xl bg-[#313131]"></div>
              <div className="flex flex-col w-full">
                <div className="space-y-3">
                  <div className="animate-pulse bg-[#313131] w-1/2 h-6 rounded-lg"></div>
                  <div className="animate-pulse bg-[#313131] w-3/4 h-6 rounded-lg"></div>
                </div>
                <div className="mt-8 flex flex-col gap-7 flex-wrap md:flex-row md:justify-between md:items-center">
                  <div className="flex gap-5 ">
                    <div className="animate-pulse bg-[#313131] w-24 h-8 rounded-lg"></div>
                    <div className="animate-pulse bg-[#313131] w-24 h-8 rounded-lg"></div>
                  </div>
                  <div className="pl-3">
                    <div className="animate-pulse bg-[#313131] w-32 h-32 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="my-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="animate-pulse bg-[#313131] w-full h-44 rounded-lg"></div>
          <div className="animate-pulse bg-[#313131] w-full h-44 rounded-lg"></div>
          <div className="animate-pulse bg-[#313131] w-full h-44 rounded-lg"></div>
          <div className="animate-pulse bg-[#313131] w-full h-44 rounded-lg"></div>
        </div>
      </div>
    </>
  );
};
